// src/pages/tryouts/TryoutDetail.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  doc, onSnapshot, collection, query, where, orderBy,
  addDoc, updateDoc, serverTimestamp, getDocs, getDoc,
} from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { useTeam } from '@/context/TeamContext';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import clsx from 'clsx';

import {
  ArrowLeftIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardDocumentCheckIcon,
  StarIcon,
  CalendarDaysIcon,
  MapPinIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

/* ─────────────────────────────────────────────
   Constants
───────────────────────────────────────────── */
const POSITIONS = ['Any', 'GK', 'Defender', 'Midfielder', 'Forward'];

const POSITION_COLORS = {
  GK:         'bg-amber-50 text-amber-700 border-amber-200',
  Defender:   'bg-[#b5f03d]/15 text-[#3d6010] border-[#b5f03d]/40',
  Midfielder: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Forward:    'bg-red-50 text-red-600 border-red-200',
  Any:        'bg-zinc-50 text-zinc-600 border-zinc-200',
};

/* ─────────────────────────────────────────────
   Helpers
───────────────────────────────────────────── */
function toDate(v) {
  if (!v) return null;
  if (v?.toDate) return v.toDate();
  if (v?.seconds) return new Date(v.seconds * 1000);
  return new Date(v);
}

function nextAvailableBib(players) {
  if (!players.length) return 1;
  const bibs = players.map((p) => Number(p.bibNumber)).filter(Boolean);
  return bibs.length ? Math.max(...bibs) + 1 : 1;
}

/* ─────────────────────────────────────────────
   Score Button Row — pitch-dark active state
───────────────────────────────────────────── */
function ScoreButtons({ criteriaId, currentScore, onScore }) {
  return (
    <div className="flex gap-1 flex-wrap">
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onScore(criteriaId, n)}
          className={clsx(
            'w-8 h-8 rounded-lg text-xs font-bold border transition-colors',
            currentScore === n
              ? 'bg-[#0d1f14] text-[#b5f03d] border-[#0d1f14]'
              : 'bg-white text-zinc-600 border-zinc-200 hover:border-[#b5f03d]'
          )}
          style={{ fontFamily: HEAD_FONT }}
        >
          {n}
        </button>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────
   TAB 1: Check-In
───────────────────────────────────────────── */
function CheckInTab({ tryout, players, teamId }) {
  const [addOpen, setAddOpen] = useState(true);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    bibNumber: '',
    position: 'Any',
    birthYear: '',
    phone: '',
  });

  // Auto-suggest bib
  useEffect(() => {
    setForm((f) => ({ ...f, bibNumber: String(nextAvailableBib(players)) }));
  }, [players.length]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleAddPlayer = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!form.bibNumber) { toast.error('Bib number is required'); return; }
    const bibNum = parseInt(form.bibNumber, 10);
    const bibTaken = players.some((p) => Number(p.bibNumber) === bibNum);
    if (bibTaken) { toast.error(`Bib #${bibNum} is already taken`); return; }

    setSaving(true);
    try {
      await addDoc(collection(db, 'tryoutPlayers'), {
        tryoutId: tryout.id,
        teamId,
        name: form.name.trim(),
        position: form.position || 'Any',
        bibNumber: bibNum,
        phone: form.phone.trim(),
        birthYear: form.birthYear ? parseInt(form.birthYear, 10) : null,
        checkedIn: true,
        checkedInAt: serverTimestamp(),
        scores: {},
        totalScore: 0,
        notes: '',
        createdAt: serverTimestamp(),
      });
      // update playerCount on tryout doc
      await updateDoc(doc(db, 'tryouts', tryout.id), {
        playerCount: players.length + 1,
      });
      toast.success(`${form.name.trim()} added & checked in!`);
      setForm({
        name: '',
        bibNumber: String(nextAvailableBib([...players, { bibNumber: bibNum }])),
        position: 'Any',
        birthYear: '',
        phone: '',
      });
    } catch (err) {
      console.error(err);
      toast.error('Failed to add player');
    } finally {
      setSaving(false);
    }
  };

  const toggleCheckIn = async (player) => {
    const now = player.checkedIn ? null : new Date();
    try {
      await updateDoc(doc(db, 'tryoutPlayers', player.id), {
        checkedIn: !player.checkedIn,
        checkedInAt: now ? serverTimestamp() : null,
      });
    } catch {
      toast.error('Failed to update check-in');
    }
  };

  const checkedInCount = players.filter((p) => p.checkedIn).length;
  const filtered = players.filter((p) =>
    !search || p.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4">
      {/* Add Player Panel */}
      <div className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] overflow-hidden">
        <button
          type="button"
          className="w-full flex items-center justify-between px-4 py-3 text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
          onClick={() => setAddOpen((v) => !v)}
          style={{ fontFamily: HEAD_FONT }}
        >
          <span>Add Player</span>
          {addOpen
            ? <ChevronUpIcon className="h-4 w-4 text-zinc-400" />
            : <ChevronDownIcon className="h-4 w-4 text-zinc-400" />
          }
        </button>

        {addOpen && (
          <form onSubmit={handleAddPlayer} className="px-4 pb-4 space-y-3 border-t border-zinc-100">
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div className="col-span-2">
                <label className="block text-xs font-medium text-zinc-600 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Full name"
                  className="w-full rounded-xl border border-zinc-200 px-3 py-2.5 text-sm text-[#0d1f14] placeholder-zinc-400 focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-zinc-600 mb-1">
                  Bib # <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="bibNumber"
                  value={form.bibNumber}
                  onChange={handleChange}
                  min={1}
                  max={99}
                  className="w-full rounded-xl border border-zinc-200 px-3 py-2.5 text-sm text-[#0d1f14] focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-zinc-600 mb-1">Position</label>
                <select
                  name="position"
                  value={form.position}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-zinc-200 px-3 py-2.5 text-sm text-[#0d1f14] focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30 bg-white"
                >
                  {POSITIONS.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-zinc-600 mb-1">Birth Year</label>
                <input
                  type="number"
                  name="birthYear"
                  value={form.birthYear}
                  onChange={handleChange}
                  placeholder="e.g. 2010"
                  min={1990}
                  max={new Date().getFullYear()}
                  className="w-full rounded-xl border border-zinc-200 px-3 py-2.5 text-sm text-[#0d1f14] placeholder-zinc-400 focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-zinc-600 mb-1">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="Optional"
                  className="w-full rounded-xl border border-zinc-200 px-3 py-2.5 text-sm text-[#0d1f14] placeholder-zinc-400 focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={saving}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97] disabled:opacity-50"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
            >
              {saving ? 'Adding…' : '+ Add & Check In'}
            </button>
          </form>
        )}
      </div>

      {/* Stats row */}
      <div className="flex items-center justify-between px-1">
        <p className="text-sm font-medium text-zinc-700">
          <span className="text-[#0d1f14] font-bold">{checkedInCount}</span>
          <span className="text-zinc-400"> / </span>
          <span>{players.length}</span>
          <span className="text-zinc-500"> checked in</span>
        </p>
        {/* Search */}
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search…"
            className="pl-8 pr-3 py-2 rounded-xl border border-zinc-200 bg-white text-sm text-[#0d1f14] placeholder-zinc-400 focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30 w-36"
          />
        </div>
      </div>

      {/* Player list */}
      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-zinc-200 bg-white p-8 text-center shadow-[0_1px_2px_0_rgba(13,31,20,0.04)]">
          <p className="text-sm text-zinc-500">
            {search ? 'No players match your search.' : 'No players added yet.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((player) => (
            <div
              key={player.id}
              className={clsx(
                'rounded-2xl border px-4 py-3 flex items-center gap-3 transition-colors shadow-[0_1px_2px_0_rgba(13,31,20,0.04)]',
                player.checkedIn
                  ? 'border-[#b5f03d]/40 bg-[#b5f03d]/10'
                  : 'border-zinc-200 bg-white hover:border-[#b5f03d]/40'
              )}
            >
              {/* Bib */}
              <span
                className="bg-[#0d1f14] text-[#b5f03d] rounded-lg px-2 py-1 text-sm font-black shrink-0 min-w-[2rem] text-center"
                style={{ fontFamily: HEAD_FONT }}
              >
                {player.bibNumber}
              </span>

              {/* Name + position */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#0d1f14] truncate">{player.name}</p>
                {player.position && player.position !== 'Any' && (
                  <span
                    className={clsx(
                      'inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide mt-0.5 border',
                      POSITION_COLORS[player.position] ?? POSITION_COLORS.Any
                    )}
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                  >
                    {player.position}
                  </span>
                )}
              </div>

              {/* Check-in button / status */}
              {player.checkedIn ? (
                <div className="flex items-center gap-1.5 text-[#3d6010] shrink-0">
                  <CheckCircleSolid className="h-5 w-5" />
                  <span
                    className="text-xs font-bold uppercase tracking-wide hidden sm:block"
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                  >
                    {player.checkedInAt
                      ? format(toDate(player.checkedInAt), 'h:mm a')
                      : 'In'}
                  </span>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => toggleCheckIn(player)}
                  className="shrink-0 inline-flex items-center gap-1.5 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-3 py-1.5 text-xs font-bold uppercase transition-colors"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                >
                  Check In
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   TAB 2: Evaluate
───────────────────────────────────────────── */
function EvaluateTab({ tryout, players }) {
  const [expandedId, setExpandedId] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const criteria = tryout.evaluationCriteria ?? [];
  const maxTotal = criteria.reduce((s, c) => s + (c.maxScore ?? 10), 0);

  const checkedInPlayers = players
    .filter((p) => p.checkedIn)
    .sort((a, b) => Number(a.bibNumber) - Number(b.bibNumber));

  const isComplete = (player) =>
    criteria.length > 0 && criteria.every((c) => (player.scores?.[c.id] ?? 0) > 0);

  const handleScore = useCallback(async (player, criteriaId, value) => {
    const newScores = { ...(player.scores || {}), [criteriaId]: value };
    const total = Object.values(newScores).reduce((s, v) => s + (v || 0), 0);
    setSavingId(player.id);
    try {
      await updateDoc(doc(db, 'tryoutPlayers', player.id), {
        [`scores.${criteriaId}`]: value,
        totalScore: total,
      });
    } catch {
      toast.error('Failed to save score');
    } finally {
      setSavingId(null);
    }
  }, []);

  const handleNotes = useCallback(async (player, notes) => {
    try {
      await updateDoc(doc(db, 'tryoutPlayers', player.id), { notes });
    } catch {
      toast.error('Failed to save notes');
    }
  }, []);

  if (checkedInPlayers.length === 0) {
    return (
      <div className="rounded-2xl border border-zinc-200 bg-white p-10 sm:p-12 text-center shadow-[0_1px_2px_0_rgba(13,31,20,0.04)]">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
          <StarIcon className="h-7 w-7 text-[#5a8a1f]" />
        </div>
        <h3
          className="text-lg font-bold uppercase tracking-wide text-[#0d1f14]"
          style={{ fontFamily: HEAD_FONT }}
        >
          No players checked in yet
        </h3>
        <p className="mt-2 text-sm text-zinc-500">
          Check in players from the Check-In tab to start evaluating them.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {checkedInPlayers.map((player) => {
        const expanded = expandedId === player.id;
        const total = player.totalScore ?? 0;
        const complete = isComplete(player);
        const isSaving = savingId === player.id;

        return (
          <div
            key={player.id}
            className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] hover:border-[#b5f03d]/40 transition-colors overflow-hidden"
          >
            {/* Player row header */}
            <button
              type="button"
              className="w-full flex items-center gap-3 px-4 py-3 text-left"
              onClick={() => setExpandedId(expanded ? null : player.id)}
            >
              <span
                className="bg-[#0d1f14] text-[#b5f03d] rounded-lg px-2 py-1 text-sm font-black shrink-0 min-w-[2rem] text-center"
                style={{ fontFamily: HEAD_FONT }}
              >
                {player.bibNumber}
              </span>
              <span className="flex-1 text-sm font-semibold text-[#0d1f14] truncate">
                {player.name}
              </span>
              {complete && (
                <span
                  className="shrink-0 inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                >
                  <CheckCircleSolid className="h-3.5 w-3.5" />
                  Done
                </span>
              )}
              <span
                className="shrink-0 text-sm font-bold text-[#0d1f14]"
                style={{ fontFamily: HEAD_FONT }}
              >
                {total}/{maxTotal}
              </span>
              {expanded
                ? <ChevronUpIcon className="h-4 w-4 text-zinc-400 shrink-0" />
                : <ChevronDownIcon className="h-4 w-4 text-zinc-400 shrink-0" />
              }
            </button>

            {/* Expanded eval form */}
            {expanded && (
              <div className="border-t border-zinc-100 px-4 pb-4 space-y-4 pt-3">
                {isSaving && (
                  <p className="text-xs text-[#5a8a1f] animate-pulse">Saving…</p>
                )}
                {criteria.map((c) => (
                  <div key={c.id}>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-xs font-medium text-zinc-600">{c.label}</label>
                      <span className="text-xs font-bold text-[#0d1f14]">
                        {player.scores?.[c.id] ?? '–'}/{c.maxScore}
                      </span>
                    </div>
                    <ScoreButtons
                      criteriaId={c.id}
                      currentScore={player.scores?.[c.id]}
                      onScore={(cid, val) => handleScore(player, cid, val)}
                    />
                  </div>
                ))}

                {/* Notes */}
                <div>
                  <label className="block text-xs font-medium text-zinc-600 mb-1">
                    Coach Notes
                  </label>
                  <textarea
                    rows={2}
                    defaultValue={player.notes || ''}
                    onBlur={(e) => handleNotes(player, e.target.value)}
                    placeholder="Optional notes about this player…"
                    className="w-full rounded-xl border border-zinc-200 px-3 py-2 text-sm text-[#0d1f14] placeholder-zinc-400 focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30 resize-none"
                  />
                </div>

                <button
                  type="button"
                  onClick={() => setExpandedId(null)}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-zinc-200 bg-white px-4 py-2.5 text-sm font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-all"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                >
                  Done
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ─────────────────────────────────────────────
   TAB 3: Results
───────────────────────────────────────────── */
function ResultsTab({ tryout, players }) {
  const criteria = tryout.evaluationCriteria ?? [];
  const maxTotal = criteria.reduce((s, c) => s + (c.maxScore ?? 10), 0);

  const evaluated = players.filter((p) => p.totalScore > 0);
  const fullyEvaluated = players.filter((p) =>
    criteria.length > 0 && criteria.every((c) => (p.scores?.[c.id] ?? 0) > 0)
  );

  const sorted = [...players].sort((a, b) => (b.totalScore ?? 0) - (a.totalScore ?? 0));

  const getBarColor = (score) => {
    const pct = maxTotal > 0 ? (score / maxTotal) * 100 : 0;
    if (pct >= 80) return 'bg-[#b5f03d]';
    if (pct >= 60) return 'bg-emerald-500';
    if (pct >= 40) return 'bg-amber-500';
    return 'bg-red-400';
  };

  const copyResults = () => {
    const dateStr = tryout.date
      ? format(toDate(tryout.date), 'MMM d, yyyy')
      : '';
    const lines = [
      `🏆 TRYOUT RESULTS — ${tryout.name}`,
      dateStr,
      '',
      'RANK | BIB | NAME | SCORE',
      ...sorted.map((p, i) =>
        `${i + 1}. #${p.bibNumber} ${p.name} — ${p.totalScore ?? 0}/${maxTotal}`
      ),
      '',
      'Generated by Scrimly',
    ];
    navigator.clipboard
      .writeText(lines.join('\n'))
      .then(() => toast.success('Results copied!'));
  };

  if (sorted.length === 0) {
    return (
      <div className="rounded-2xl border border-zinc-200 bg-white p-10 sm:p-12 text-center shadow-[0_1px_2px_0_rgba(13,31,20,0.04)]">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
          <ClipboardDocumentCheckIcon className="h-7 w-7 text-[#5a8a1f]" />
        </div>
        <h3
          className="text-lg font-bold uppercase tracking-wide text-[#0d1f14]"
          style={{ fontFamily: HEAD_FONT }}
        >
          No players yet
        </h3>
        <p className="mt-2 text-sm text-zinc-500">
          Add players and score them to see results here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary row */}
      <div className="flex items-center justify-between px-1">
        <p className="text-sm text-zinc-600">
          <span className="font-bold text-[#0d1f14]">{fullyEvaluated.length}</span>
          {' of '}
          <span className="font-bold text-[#0d1f14]">{players.length}</span>
          {' players fully evaluated'}
        </p>
        <button
          type="button"
          onClick={copyResults}
          className="inline-flex items-center gap-2 rounded-xl border border-zinc-200 bg-white px-3 py-1.5 text-xs font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-all"
          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
        >
          Copy Results
        </button>
      </div>

      {/* Leaderboard */}
      <div className="space-y-2">
        {sorted.map((player, idx) => {
          const score = player.totalScore ?? 0;
          const pct = maxTotal > 0 ? (score / maxTotal) * 100 : 0;
          return (
            <div
              key={player.id}
              className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] hover:border-[#b5f03d]/40 transition-colors px-4 py-3"
            >
              <div className="flex items-center gap-3">
                {/* Rank */}
                <span
                  className="text-sm font-bold text-zinc-400 w-6 shrink-0 text-center"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  {idx + 1}
                </span>
                {/* Bib */}
                <span
                  className="bg-[#0d1f14] text-[#b5f03d] rounded-lg px-2 py-1 text-sm font-black shrink-0 min-w-[2rem] text-center"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  {player.bibNumber}
                </span>
                {/* Name + position */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#0d1f14] truncate">{player.name}</p>
                  {player.position && player.position !== 'Any' && (
                    <span
                      className={clsx(
                        'inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide border mt-0.5',
                        POSITION_COLORS[player.position] ?? POSITION_COLORS.Any
                      )}
                      style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                    >
                      {player.position}
                    </span>
                  )}
                </div>
                {/* Score */}
                <span
                  className="shrink-0 text-sm font-bold text-[#0d1f14]"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  {score}/{maxTotal}
                </span>
              </div>

              {/* Score bar */}
              {maxTotal > 0 && (
                <div className="mt-2 h-2 rounded-full bg-zinc-100 overflow-hidden">
                  <div
                    className={clsx('h-full rounded-full transition-all', getBarColor(score))}
                    style={{ width: `${Math.min(pct, 100)}%` }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Export to Program Modal
───────────────────────────────────────────── */
function ExportToProgramModal({ players, teamId, onClose }) {
  const [programs, setPrograms]   = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedDivId, setSelectedDivId]     = useState('');
  const [selectedPlayerIds, setSelectedPlayerIds] = useState(new Set(players.map(p => p.id)));
  const [exporting, setExporting] = useState(false);
  const [loadingPrograms, setLoadingPrograms] = useState(true);

  // Load programs for this team's club
  useEffect(() => {
    if (!teamId) { setLoadingPrograms(false); return; }
    (async () => {
      try {
        // get team to find clubId
        const { getDoc: firestoreGetDoc, getDocs: firestoreGetDocs, doc: firestoreDoc, collection: firestoreCol, query: firestoreQuery, where: firestoreWhere } = await import('firebase/firestore');
        const { db: firestoreDb } = await import('@/firebase/firebase');
        const teamSnap = await firestoreGetDoc(firestoreDoc(firestoreDb, 'teams', teamId));
        const clubId = teamSnap.exists() ? teamSnap.data().clubId : null;
        if (!clubId) { setLoadingPrograms(false); return; }
        const snap = await firestoreGetDocs(firestoreQuery(
          firestoreCol(firestoreDb, 'programs'),
          firestoreWhere('clubId', '==', clubId),
          firestoreWhere('status', '==', 'open')
        ));
        const progs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setPrograms(progs);
        if (progs.length > 0) {
          setSelectedProgram(progs[0]);
          setSelectedDivId(progs[0].divisions?.[0]?.id ?? '');
        }
      } catch (e) {
        console.error('ExportToProgramModal load error:', e);
      } finally {
        setLoadingPrograms(false);
      }
    })();
  }, [teamId]);

  const togglePlayer = (id) => {
    setSelectedPlayerIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleExport = async () => {
    if (!selectedProgram || !selectedPlayerIds.size) return;
    const div = selectedProgram.divisions?.find(d => d.id === selectedDivId);
    const { addDoc: firestoreAddDoc, collection: firestoreCol, serverTimestamp: firestoreTs } = await import('firebase/firestore');
    const { db: firestoreDb } = await import('@/firebase/firebase');
    try {
      setExporting(true);
      const selected = players.filter(p => selectedPlayerIds.has(p.id));
      await Promise.all(selected.map(async (p) => {
        await firestoreAddDoc(firestoreCol(firestoreDb, 'registrations'), {
          programId:   selectedProgram.id,
          divisionId:  selectedDivId,
          clubId:      selectedProgram.clubId,
          teamId:      div?.teamId ?? null,
          seasonId:    null,
          playerName:  p.name,
          playerDob:   null,
          playerGender: null,
          parentName:  '',
          parentEmail: '',
          parentPhone: p.phone ?? null,
          pricing:     { finalAmount: 0, baseFee: 0, isEarlyBird: false, label: 'Coach Added' },
          amountDue:   0,
          waiverSignatures: [],
          status: 'confirmed',
          source: 'tryout_export',
          tryoutId: p.tryoutId,
          createdAt: firestoreTs(),
          updatedAt: firestoreTs(),
        });
      }));
      toast.success(`${selected.length} player${selected.length !== 1 ? 's' : ''} added to ${selectedProgram.name}!`);
      onClose();
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-zinc-200 p-6 space-y-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2
            className="text-base font-bold uppercase tracking-tight text-[#0d1f14]"
            style={{ fontFamily: HEAD_FONT }}
          >
            Send Players to Program
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-zinc-400 hover:text-[#0d1f14] transition-colors"
          >
            ✕
          </button>
        </div>

        {loadingPrograms ? (
          <p className="text-sm text-zinc-400">Loading programs…</p>
        ) : programs.length === 0 ? (
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-4">
            <p className="text-sm text-amber-800 font-medium">No open programs found</p>
            <p className="text-xs text-amber-600 mt-1">Create an open program in Club Admin → Programs first.</p>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              <div>
                <label
                  className="block text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-1"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  Program
                </label>
                <select
                  className="w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm text-[#0d1f14] focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30"
                  value={selectedProgram?.id ?? ''}
                  onChange={(e) => {
                    const p = programs.find(x => x.id === e.target.value);
                    setSelectedProgram(p);
                    setSelectedDivId(p?.divisions?.[0]?.id ?? '');
                  }}
                >
                  {programs.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              {(selectedProgram?.divisions?.length ?? 0) > 0 && (
                <div>
                  <label
                    className="block text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-1"
                    style={{ fontFamily: HEAD_FONT }}
                  >
                    Division
                  </label>
                  <select
                    className="w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm text-[#0d1f14] focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30"
                    value={selectedDivId}
                    onChange={(e) => setSelectedDivId(e.target.value)}
                  >
                    {selectedProgram.divisions.map(d => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label
                  className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  Players to export
                </label>
                <button
                  type="button"
                  onClick={() => setSelectedPlayerIds(
                    selectedPlayerIds.size === players.length ? new Set() : new Set(players.map(p => p.id))
                  )}
                  className="text-xs font-semibold text-[#5a8a1f] hover:text-[#3d6010] hover:underline transition-colors"
                >
                  {selectedPlayerIds.size === players.length ? 'Deselect all' : 'Select all'}
                </button>
              </div>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {players.map(p => (
                  <label
                    key={p.id}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[#b5f03d]/10 cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selectedPlayerIds.has(p.id)}
                      onChange={() => togglePlayer(p.id)}
                      className="h-4 w-4 rounded border-zinc-300 text-[#94cc28] focus:ring-[#b5f03d]"
                    />
                    <span className="text-sm text-[#0d1f14] flex-1">{p.name}</span>
                    {p.totalScore > 0 && (
                      <span className="text-xs font-semibold text-zinc-500">
                        {p.totalScore.toFixed(1)} pts
                      </span>
                    )}
                  </label>
                ))}
              </div>
            </div>

            <div className="rounded-xl bg-[#f7faf5] border border-zinc-200 p-3 text-xs text-zinc-600">
              Players will be added as <strong className="text-[#0d1f14]">confirmed</strong> registrations with $0 fee. You can update their status and fee in the program's registration dashboard.
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                disabled={exporting || !selectedPlayerIds.size}
                onClick={handleExport}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97] disabled:opacity-50"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
              >
                {exporting ? 'Exporting…' : `Add ${selectedPlayerIds.size} Player${selectedPlayerIds.size !== 1 ? 's' : ''} to Program`}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center gap-2 rounded-xl border border-zinc-200 bg-white px-4 py-2.5 text-sm font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-all"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Main: TryoutDetail
───────────────────────────────────────────── */
const TABS = ['Check-In', 'Evaluate', 'Results'];

export default function TryoutDetail() {
  const { tryoutId } = useParams();
  const navigate = useNavigate();
  const { currentTeam } = useTeam();

  const [tryout, setTryout] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('Check-In');
  const [togglingStatus, setTogglingStatus] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  // Live tryout doc
  useEffect(() => {
    if (!tryoutId) return;
    const unsub = onSnapshot(
      doc(db, 'tryouts', tryoutId),
      (snap) => {
        if (snap.exists()) setTryout({ id: snap.id, ...snap.data() });
        setLoading(false);
      },
      (err) => {
        console.error('[TryoutDetail] tryout', err);
        setLoading(false);
      }
    );
    return unsub;
  }, [tryoutId]);

  // Live players
  useEffect(() => {
    if (!tryoutId) return;
    const q = query(
      collection(db, 'tryoutPlayers'),
      where('tryoutId', '==', tryoutId),
      orderBy('bibNumber', 'asc')
    );
    const unsub = onSnapshot(
      q,
      (snap) => setPlayers(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error('[TryoutDetail] players', err)
    );
    return unsub;
  }, [tryoutId]);

  const toggleStatus = async () => {
    if (!tryout) return;
    const next = tryout.status === 'open' ? 'closed' : 'open';
    setTogglingStatus(true);
    try {
      await updateDoc(doc(db, 'tryouts', tryoutId), {
        status: next,
        updatedAt: serverTimestamp(),
      });
      toast.success(`Tryout ${next === 'open' ? 'opened' : 'closed'}.`);
    } catch {
      toast.error('Failed to update status');
    } finally {
      setTogglingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f7faf5]">
        <div className="p-4 sm:p-6 max-w-2xl mx-auto space-y-4">
          <div className="h-8 w-48 rounded-xl bg-zinc-100 animate-pulse" />
          <div className="h-32 rounded-2xl bg-zinc-100 animate-pulse" />
        </div>
      </div>
    );
  }

  if (!tryout) {
    return (
      <div className="min-h-screen bg-[#f7faf5]">
        <div className="p-4 sm:p-6 text-center max-w-2xl mx-auto">
          <p className="text-sm text-zinc-500">Tryout not found.</p>
          <button
            type="button"
            onClick={() => navigate('/tryouts')}
            className="mt-4 inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-[#5a8a1f] transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4" /> Back
          </button>
        </div>
      </div>
    );
  }

  const isOpen = tryout.status === 'open';
  const dateVal = toDate(tryout.date);

  return (
    <div className="min-h-screen bg-[#f7faf5]">
      <div className="p-4 sm:p-6 lg:p-8 max-w-2xl mx-auto">

        {/* Back */}
        <button
          type="button"
          onClick={() => navigate('/tryouts')}
          className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-[#5a8a1f] mb-4 transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          All Tryouts
        </button>

        {/* Header — pitch-dark with lime accents */}
        <div className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] mb-5 overflow-hidden">
          <div className="px-5 pt-5 pb-5 bg-[#0d1f14] relative overflow-hidden">
            {/* Subtle lime grid */}
            <div
              className="pointer-events-none absolute inset-0 opacity-[0.05]"
              style={{
                backgroundImage:
                  'linear-gradient(to right, #b5f03d 1px, transparent 1px), linear-gradient(to bottom, #b5f03d 1px, transparent 1px)',
                backgroundSize: '32px 32px',
              }}
            />
            <div className="relative flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
                  <ClipboardDocumentCheckIcon className="h-5 w-5 text-[#b5f03d]" />
                </div>
                <div className="min-w-0">
                  <p
                    className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#b5f03d] mb-1"
                    style={{ fontFamily: HEAD_FONT }}
                  >
                    Tryout
                  </p>
                  <h1
                    className="uppercase text-white leading-[1.05] truncate"
                    style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 22 }}
                  >
                    {tryout.name}
                  </h1>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                <span
                  className={clsx(
                    'inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide border',
                    isOpen
                      ? 'bg-[#b5f03d]/15 text-[#b5f03d] border-[#b5f03d]/40'
                      : 'bg-white/10 text-white/70 border-white/20'
                  )}
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                >
                  {isOpen ? 'Open' : 'Closed'}
                </span>
                <button
                  type="button"
                  onClick={toggleStatus}
                  disabled={togglingStatus}
                  className="text-[10px] font-bold uppercase tracking-wide text-white/70 hover:text-[#b5f03d] underline transition-colors disabled:opacity-50"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                >
                  {togglingStatus ? '…' : isOpen ? 'Close Tryout' : 'Reopen Tryout'}
                </button>
              </div>
            </div>
          </div>

          {/* Details */}
          <div className="px-5 py-4 space-y-3">
            {dateVal && (
              <div className="flex items-center gap-3 text-sm flex-wrap">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
                  <CalendarDaysIcon className="h-4 w-4 text-[#5a8a1f]" />
                </div>
                <span>
                  <span className="font-semibold text-[#0d1f14]">{format(dateVal, 'EEEE, MMMM d, yyyy')}</span>
                  {tryout.time && <span className="text-zinc-500">{' · '}{tryout.time}</span>}
                </span>
              </div>
            )}
            {tryout.location && (
              <div className="flex items-start gap-3 text-sm">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 mt-0.5">
                  <MapPinIcon className="h-4 w-4 text-[#5a8a1f]" />
                </div>
                <span className="text-[#0d1f14]">{tryout.location}</span>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] mb-5 overflow-hidden">
          {TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={clsx(
                'flex-1 py-3 text-sm font-bold uppercase tracking-wide transition-colors',
                activeTab === tab
                  ? 'bg-[#0d1f14] text-[#b5f03d]'
                  : 'text-zinc-600 hover:bg-[#b5f03d]/10 hover:text-[#0d1f14]'
              )}
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'Check-In' && (
          <CheckInTab tryout={tryout} players={players} teamId={currentTeam?.id} />
        )}
        {activeTab === 'Evaluate' && (
          <EvaluateTab tryout={tryout} players={players} />
        )}
        {activeTab === 'Results' && (
          <>
            <ResultsTab tryout={tryout} players={players} />
            <div className="mt-4">
              <button
                type="button"
                onClick={() => setShowExportModal(true)}
                className="inline-flex items-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97]"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
              >
                <PaperAirplaneIcon className="h-4 w-4" />
                Send to Program
              </button>
            </div>
            {showExportModal && (
              <ExportToProgramModal
                players={players}
                teamId={currentTeam?.id}
                onClose={() => setShowExportModal(false)}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
